import React from 'react';
import PropTypes from 'prop-types';
import ProductCard from '../../ProductCard';
import Button from '../../Button';
import ProductsGridTile from './ProductsGridTile';
import composeGridEntries, { isValidPosition } from './composeGridEntries';
import './ProductsGrid.css';

/**
 * ProductsGrid Component
 *
 * Responsive grid layout for ProductCard items.
 * Figma node: 3871:1020
 *
 * Layout defaults:
 * - ≥768px: 3 columns for exactly 3 cards, otherwise 4 (override: `desktopColumns`)
 * - `layout="playful"`: alternating desktop offsets (3-up or 4-up)
 * - 376–767px: 2 columns; an odd number of cards spans the LAST card full width
 *   (`oddCard="first"`: the FIRST card)
 * - 0–375px: 1 column
 *
 * Gap defaults:
 * - Row gap (vertical): var(--space-ui-large-s) = 48px
 * - Column gap (horizontal): var(--space-ui-medium-l) = 32px
 *
 * All values are overridable via props.
 *
 * Opt-in content (Figma OVER-LL 2699:18385 / 2707:18924 / 2699:16566):
 * `tiles` places text / image tiles on positions between the cards, `spans`
 * widens the entry on a position to 2 desktop columns. Without them the grid
 * renders exactly as it always has (none of the new classes appear).
 */

/* Item → ProductCard props. An item's OWN props win over cardProps — but only
   the ones it actually carries: an absent prop must not undo the shared value */
const toCardProps = ({ image, imageAlt, secondImage, secondImageAlt, title, description, price, priceState, originalPrice, priceBadgeText, priceSavingsText, colors, moreLabel: itemMoreLabel, selected, selectedLabel, discountBadgeText, imageAspectRatio, imageFit, href, onClick, className: itemClassName }, moreLabel) => (
  Object.fromEntries(Object.entries({
    image,
    imageAlt,
    secondImage,
    secondImageAlt,
    title,
    description,
    priceValue: price,
    priceState,
    originalPrice,
    priceBadgeText,
    priceSavingsText,
    colors,
    moreLabel: itemMoreLabel || moreLabel,
    selected,
    selectedLabel,
    discountBadgeText,
    imageAspectRatio,
    imageFit,
    href,
    onClick,
    className: itemClassName,
  }).filter(([, value]) => value !== undefined))
);

const devWarn = (message) => {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(message);
  }
};

/* Map DS spacing token shorthand → CSS variable */
const GAP_TOKENS = {
  'small-s': 'var(--space-ui-small-s)',
  'small-m': 'var(--space-ui-small-m)',
  'small-l': 'var(--space-ui-small-l)',
  'medium-s': 'var(--space-ui-medium-s)',
  'medium-m': 'var(--space-ui-medium-m)',
  'medium-l': 'var(--space-ui-medium-l)',
  'large-s': 'var(--space-ui-large-s)',
  'large-m': 'var(--space-ui-large-m)',
  'large-l': 'var(--space-ui-large-l)',
};

/**
 * Resolve gap value: DS token shorthand → CSS var, or pass through raw CSS value.
 */
const resolveGap = (value) => {
  if (!value) return undefined;
  return GAP_TOKENS[value] || value;
};

/**
 * Normalise a gap prop: string → same value for all breakpoints; object → per-breakpoint.
 * Returns { base, sm, md } with resolved CSS values.
 */
const normaliseGap = (prop, fallback) => {
  if (typeof prop === 'object' && prop !== null) {
    return {
      base: resolveGap(prop.base) || resolveGap(fallback),
      sm: resolveGap(prop.sm),
      md: resolveGap(prop.md),
    };
  }
  return { base: resolveGap(prop || fallback) };
};

const ProductsGrid = ({
  children,
  items,
  brand,
  theme = 'light',
  caption,
  captionVariant = 'default',
  title,
  subtitle,
  align = 'center',
  ctaLabel,
  ctaVariant,
  ctaSize = 'primary',
  ctaIcon,
  ctaHref,
  ctaOnClick,
  paddingTop = 'large-m',
  paddingBottom = 'large-m',
  headerGap,
  background,
  backgroundImage,
  backgroundSize = 'cover',
  backgroundPosition = 'center',
  rowGap = { base: 'medium-l', sm: 'medium-l', md: 'large-s' },
  columnGap = { base: 'medium-s', sm: 'medium-s', md: 'medium-l' },
  columns,
  desktopColumns = 'auto',
  lastRow = 'start',
  centerIncomplete = false,
  oddCard = 'last',
  layout = 'grid',
  stagger = false,
  cardProps,
  captionStyle = 'h6',
  moreLabel,
  tiles,
  spans,
  className = '',
  style: styleProp,
  ...rest
}) => {
  /* Content tiles / wide entries switch the grid to its slot layout. Empty
     values count as absent, so the default render path stays untouched. */
  const hasSlots = Boolean(items) && ((tiles && tiles.length > 0) || (spans && Object.keys(spans).length > 0));

  const cardCount = hasSlots
    ? items.length + (tiles || []).filter((tile) => isValidPosition(tile.position)).length
    : (items ? items.length : React.Children.count(children));

  /* Explicit per-breakpoint overrides. A number pins every breakpoint; an object
   * contributes only the keys it names. Left undefined by default so an omitted
   * `columns` is distinguishable from one that deliberately asks for 4. */
  const explicit = typeof columns === 'number'
    ? { base: columns, sm: columns, md: columns }
    : (columns || {});

  /* Desktop column count — most specific wins:
   *   columns.md  →  desktopColumns  →  auto (exactly 3 cards ⇒ 3-up, else 4-up) */
  const autoMd = cardCount === 3 ? 3 : 4;
  const cols = {
    base: explicit.base ?? 1,
    sm: explicit.sm ?? 2,
    md: explicit.md ?? (desktopColumns === 'auto' ? autoMd : desktopColumns),
  };

  const rg = normaliseGap(rowGap, 'medium-l');
  const cg = normaliseGap(columnGap, 'medium-l');

  /* Center an incomplete desktop row: fewer items than md columns → render
   * exactly that many tracks, each sized like a full-grid track, centered.
   * Card width stays identical to the complete-row layout. */
  const wantsCenter = lastRow === 'center' || centerIncomplete;
  const centerMd = Boolean(!hasSlots && wantsCenter && items && items.length > 0 && items.length < cols.md);
  if (hasSlots && wantsCenter) {
    devWarn('ProductsGrid: lastRow="center" is not available with tiles / spans — the last row stays left-aligned.');
  }

  /* Playful layout (Figma OVER-LL Editions): the desktop cards drop by
   * alternating offsets. The nth-child rhythm is written per column count, so
   * the marker class carries it — 3-up and 4-up today. `stagger` is the old
   * boolean spelling of layout="playful". */
  const playful = layout === 'playful' || stagger;
  const playfulCols = playful && !hasSlots && [3, 4].includes(cols.md) ? cols.md : null;
  if (playful && hasSlots) {
    devWarn('ProductsGrid: layout="playful" is not available with tiles / spans (its offsets count cards by position) — rendering the flat grid.');
  } else if (playful && !playfulCols && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(`ProductsGrid: layout="playful" supports 3 or 4 desktop columns, got ${cols.md} — rendering the flat grid.`);
  }

  /* Odd-count mobile row (376–767px): an odd number of cards leaves an orphan in
   * the last 2-column row, so the LAST card spans both columns instead — 3 cards
   * read as a pair above a full-width card. A lone card keeps its normal width,
   * and the span only makes sense in a 2-column mobile grid. */
  const oddFullSm = !hasSlots && cardCount > 1 && cardCount % 2 === 1 && cols.sm === 2;
  /* `oddCard` picks WHICH card takes that full row: the last (default) or the first
   * (the listing's lead product, then the pairs under it). Phones only — the desktop
   * grid, `lastRow`, `layout` and `columns` never see it; with tiles / spans
   * `composeGridEntries` applies it to every odd run of cards between the full rows. */
  const lastFullSm = oddFullSm && oddCard !== 'first';
  const firstFullSm = oddFullSm && oddCard === 'first';

  const cssVars = {
    '--products-grid-row-gap': rg.base,
    '--products-grid-col-gap': cg.base,
    ...(rg.sm && { '--products-grid-row-gap-sm': rg.sm }),
    ...(rg.md && { '--products-grid-row-gap-md': rg.md }),
    ...(cg.sm && { '--products-grid-col-gap-sm': cg.sm }),
    ...(cg.md && { '--products-grid-col-gap-md': cg.md }),
    '--products-grid-cols': cols.base,
    '--products-grid-cols-sm': cols.sm,
    '--products-grid-cols-md': cols.md,
    ...(centerMd && { '--products-grid-count': items.length }),
    ...styleProp,
  };

  /* Section-level styles: padding & background */
  const sectionStyle = {
    ...(paddingTop && { '--pgb-pt': resolveGap(paddingTop) }),
    ...(paddingBottom && { '--pgb-pb': resolveGap(paddingBottom) }),
    ...(headerGap && { '--pgb-header-gap': resolveGap(headerGap) }),
    ...(background && { '--pgb-bg': background }),
    ...(backgroundImage && {
      '--pgb-bg-image': `url(${backgroundImage})`,
      '--pgb-bg-size': backgroundSize,
      '--pgb-bg-position': backgroundPosition,
    }),
  };

  const gridClass = [
    'ds-products-grid',
    centerMd ? 'ds-products-grid--center-md' : '',
    lastFullSm ? 'ds-products-grid--last-full-sm' : '',
    firstFullSm ? 'ds-products-grid--first-full-sm' : '',
    playfulCols ? `ds-products-grid--playful-${playfulCols}` : '',
    hasSlots ? 'ds-products-grid--slots' : '',
  ].filter(Boolean).join(' ');

  const rootClass = [
    'ds-products-grid-block',
    theme === 'dark' ? 'ds-products-grid-block--dark' : '',
    className,
  ].filter(Boolean).join(' ');

  if (!items && ((tiles && tiles.length > 0) || (spans && Object.keys(spans).length > 0))) {
    devWarn('ProductsGrid: tiles / spans need `items` — they are ignored in children mode.');
  }

  /* Slot layout: products + tiles in position order, each entry carrying its
     layout classes (see composeGridEntries + the slot section of the CSS) */
  const renderSlots = () => {
    const { entries, warnings } = composeGridEntries({ items, tiles: tiles || [], spans: spans || {}, colsSm: cols.sm, colsMd: cols.md, oddCard });
    warnings.forEach(devWarn);
    return entries.map(({ kind, key, data, span, fullSm }) => {
      const layoutClass = [
        'ds-products-grid__item',
        span > 1 ? 'ds-products-grid__item--span-2' : '',
        fullSm ? 'ds-products-grid__item--full-sm' : '',
      ].filter(Boolean).join(' ');
      if (kind === 'tile') {
        const { id, position, className: tileClassName, ...tileProps } = data;
        return <ProductsGridTile key={key} {...tileProps} className={[tileClassName, `ds-products-grid__tile--${tileProps.variant === 'image' ? 'image' : 'text'}`, layoutClass].filter(Boolean).join(' ')} />;
      }
      const own = toCardProps(data, moreLabel);
      const shared = { ...cardProps };
      /* A wide card takes its photo height from the grid (the same height as
         its neighbours' photos), never from an aspect ratio of its own */
      if (span > 1) {
        delete own.imageAspectRatio;
        delete shared.imageAspectRatio;
      }
      const ownClassName = own.className ?? shared.className;
      return <ProductCard key={key} {...shared} {...own} className={[ownClassName, layoutClass].filter(Boolean).join(' ')} />;
    });
  };

  /* Render items via ProductCard when provided, otherwise use children */
  const content = hasSlots
    ? renderSlots()
    : items
      ? items.map((item) => <ProductCard key={item.id} {...cardProps} {...toCardProps(item, moreLabel)} />)
      : children;

  const hasHeader = caption || title || subtitle;
  const headerAlign = ['left', 'center', 'right'].includes(align) ? align : 'center';

  /* Resolve CTA variant: explicit prop > theme default */
  const resolvedCtaVariant = ctaVariant || (theme === 'dark' ? 'white' : 'outline');

  return (
    <section className={rootClass} style={sectionStyle} {...(brand ? { 'data-brand': brand } : {})} {...rest}>
      <div className="ds-products-grid-block__container">
        {hasHeader && (
          <div className={`ds-products-grid-block__header ds-products-grid-block__header--${headerAlign}`}>
            {caption && (
              <p className={`ds-products-grid-block__caption ds-products-grid-block__caption--${captionVariant} ${captionStyle === 'body' ? 'ds-products-grid-block__caption--body' : 'typo-h6'}`}>{caption}</p>
            )}
            {title && <h2 className="ds-products-grid-block__title typo-h2-editorial">{title}</h2>}
            {subtitle && <p className="ds-products-grid-block__subtitle typo-body">{subtitle}</p>}
          </div>
        )}
        <div className={gridClass} style={cssVars}>
          {content}
        </div>
        {ctaLabel && (
          <div className="ds-products-grid-block__cta">
            <Button
              size={ctaSize}
              variant={resolvedCtaVariant}
              iconRight={ctaIcon}
              href={ctaHref}
              onClick={ctaOnClick}
            >
              {ctaLabel}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

ProductsGrid.propTypes = {
  /** Brand override — sets data-brand on the section so all child DS components switch styling. */
  brand: PropTypes.oneOf(['CLS', 'ABC', 'Overall', 'Crownstudent']),
  /** Color theme. "dark" inverts text to white for use on dark/image backgrounds. Default: "light" */
  theme: PropTypes.oneOf(['light', 'dark']),
  /** Optional caption above the title (renders as p typo-h6, gray-5). Figma node: 8746:73141 */
  caption: PropTypes.string,
  /** Caption colour: "default" gray-5, "success" ui-success (e.g. "SPAR OP TIL 20%"), "error" ui-error (e.g. "TRENDING") */
  captionVariant: PropTypes.oneOf(['default', 'success', 'error']),
  /** Caption type treatment: "h6" (default) or "body" — P.Regular uppercase gray-6, the OVER/LL eyebrow (Figma 2217:151809) */
  captionStyle: PropTypes.oneOf(['h6', 'body']),
  /** Horizontal alignment of the title block (caption + title + subtitle). Default: "center" */
  align: PropTypes.oneOf(['left', 'center', 'right']),
  /** Optional section title (renders as h2 typo-h2-editorial) */
  title: PropTypes.string,
  /** Optional section subtitle (renders as p typo-body, below title) */
  subtitle: PropTypes.string,
  /** CTA button label. If provided, renders a centered button below the grid. */
  ctaLabel: PropTypes.string,
  /** CTA button variant. Default: "white" for dark theme, "outline" for light. */
  ctaVariant: PropTypes.string,
  /** CTA button size (default: "primary") */
  ctaSize: PropTypes.string,
  /** CTA button icon (React element, rendered as iconRight) */
  ctaIcon: PropTypes.node,
  /** CTA button href (makes it a link) */
  ctaHref: PropTypes.string,
  /** CTA button click handler */
  ctaOnClick: PropTypes.func,
  /** Section padding-top. DS token shorthand ("large-m") or raw CSS value. Default: "large-m" */
  paddingTop: PropTypes.string,
  /** Section padding-bottom. DS token shorthand ("large-m") or raw CSS value. Default: "large-m" */
  paddingBottom: PropTypes.string,
  /** Space between the header (caption / title / subtitle) and the grid. DS token shorthand ("medium-l") or raw CSS value. Default: "large-s" */
  headerGap: PropTypes.string,
  /** Section background. Any CSS value (e.g. "var(--color-gray-0)"). Default: transparent */
  background: PropTypes.string,
  /** Background image URL. When provided, renders as cover/center/no-repeat by default. */
  backgroundImage: PropTypes.string,
  /** Background image size. Default: "cover" */
  backgroundSize: PropTypes.string,
  /** Background image position. Default: "center" */
  backgroundPosition: PropTypes.string,
  /** ProductCard elements (or any card-like children). Used when items is not provided. */
  children: PropTypes.node,
  /**
   * Product data array. When provided, ProductCard components are rendered automatically.
   * Each item: { id, image, imageAlt, title, description, price, priceState,
   *   originalPrice, priceBadgeText, priceSavingsText, colors, selected, selectedLabel, href, onClick, className }
   */
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    image: PropTypes.string,
    imageAlt: PropTypes.string,
    secondImage: PropTypes.string,
    secondImageAlt: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    price: PropTypes.string,
    priceState: PropTypes.oneOf(['default', 'discounted', 'included']),
    originalPrice: PropTypes.string,
    priceBadgeText: PropTypes.string,
    priceSavingsText: PropTypes.string,
    colors: PropTypes.arrayOf(PropTypes.string),
    moreLabel: PropTypes.string,
    selected: PropTypes.bool,
    selectedLabel: PropTypes.string,
    discountBadgeText: PropTypes.string,
    imageAspectRatio: PropTypes.string,
    href: PropTypes.string,
    onClick: PropTypes.func,
    className: PropTypes.string,
  })),
  /**
   * Vertical (row) gap between cards.
   * String: same gap for all breakpoints. DS token ("large-s") or CSS ("48px").
   * Object: { base, sm, md } — per-breakpoint gap.
   * Default: "large-s" → var(--space-ui-large-s) = 48px
   */
  rowGap: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({ base: PropTypes.string, sm: PropTypes.string, md: PropTypes.string }),
  ]),
  /**
   * Horizontal (column) gap between cards.
   * String: same gap for all breakpoints. DS token ("medium-l") or CSS ("32px").
   * Object: { base, sm, md } — per-breakpoint gap.
   * Default: "medium-l" → var(--space-ui-medium-l) = 32px
   */
  columnGap: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({ base: PropTypes.string, sm: PropTypes.string, md: PropTypes.string }),
  ]),
  /**
   * Low-level per-breakpoint column override.
   * Object: { base, sm, md } — base ≤375, sm 376–767, md ≥768; only the keys
   * given are applied (defaults: base 1, sm 2).
   * Number: same column count at all breakpoints.
   * `columns.md` outranks `desktopColumns`; prefer `desktopColumns` for the
   * common 3-vs-4 choice and reach for this only to change `base`/`sm`.
   */
  columns: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.shape({
      base: PropTypes.number,
      sm: PropTypes.number,
      md: PropTypes.number,
    }),
  ]),
  /**
   * Desktop (≥768px) columns — the 3-vs-4 setting.
   * "auto" (default): exactly 3 cards render 3-up, every other count 4-up.
   * A number pins the count regardless of how many cards there are.
   * Outranked by an explicit `columns.md`.
   */
  desktopColumns: PropTypes.oneOfType([
    PropTypes.oneOf(['auto']),
    PropTypes.number,
  ]),
  /**
   * A short final desktop row: "start" (default) leaves it left-aligned,
   * "center" renders exactly that many tracks (same card width as a full row)
   * centred horizontally. Desktop (≥768px) only; requires `items`.
   */
  lastRow: PropTypes.oneOf(['start', 'center']),
  /** @deprecated Use `lastRow="center"`. */
  centerIncomplete: PropTypes.bool,
  /**
   * Phones (376–767px, 2-column grid), ODD card count only: which card takes the
   * full row that the odd count leaves — "last" (default, the automatic rule) or
   * "first" (the lead product on top, the pairs under it; CLS-DK open shop
   * listing, a per-listing setting). Even counts, a lone card and other
   * `columns.sm` values ignore it; the desktop grid never changes. With
   * `tiles` / `spans` it applies to every odd run of cards between the full rows.
   */
  oddCard: PropTypes.oneOf(['last', 'first']),
  /**
   * "grid" (default) or "playful" (Figma OVER-LL 2217:151809): on desktop the
   * cards drop by alternating offsets — in a row of three the middle card by
   * medium-l, every second row its 2nd and 3rd by medium-m / small-l; 4-up
   * cycles the same three offsets. Mobile stays flush. Offsets are the
   * `--products-grid-playful-{1,2,3}` custom properties.
   */
  layout: PropTypes.oneOf(['grid', 'playful']),
  /** @deprecated Use `layout="playful"`. */
  stagger: PropTypes.bool,
  /** Props merged into EVERY rendered ProductCard (an item's own props win) — e.g. `{ frameMark }` */
  cardProps: PropTypes.object,
  /** Localized word for the ProductCard "+N more" swatch-overflow badge, applied to every card (item-level moreLabel overrides) */
  moreLabel: PropTypes.string,
  /**
   * Opt-in content tiles placed between the cards (Figma OVER-LL: text
   * 2699:18559, image 2707:18937). `position` is the 1-based slot in the FINAL
   * grid (cards + tiles); tiles on one position keep their order, a position
   * past the end appends the tile, an invalid one skips it (dev warning).
   * Needs `items`. With tiles (or spans) `layout="playful"` and
   * `lastRow="center"` are off. Mobile: a tile fills its row.
   */
  tiles: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    position: PropTypes.number.isRequired,
    variant: PropTypes.oneOf(['text', 'image']),
    title: PropTypes.string,
    titleAs: PropTypes.elementType,
    body: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    image: PropTypes.string,
    imageAlt: PropTypes.string,
    linkLabel: PropTypes.string,
    href: PropTypes.string,
    onClick: PropTypes.func,
    className: PropTypes.string,
  })),
  /**
   * Opt-in wide entries: `{ [position]: 2 }` — the card or tile on that slot of
   * the final grid takes 2 desktop columns, its photo as tall as a single
   * card's (Figma OVER-LL 2699:16575); mobile: a full row of the same height.
   * Needs `items`. A wide entry that would leave a gap in its desktop row is
   * reported in dev, never reordered.
   */
  spans: PropTypes.objectOf(PropTypes.oneOf([1, 2])),
  /** Additional CSS class */
  className: PropTypes.string,
  /** Inline style overrides (merged with CSS vars) */
  style: PropTypes.object,
};

export default ProductsGrid;
